import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { Lobby } from "../../services/generated/client.js";
import { appUrl } from "../../services/serverOrigin.js";
import { ConnectTv } from "./ConnectTv.js";
import { cn } from "../../lib/utils.js";

/**
 * `classic` · `deathmatch` · `team`, in the order the picker lists them.
 *
 * The wire value is the key, and the copy lives at `mode.<key>` in the locale
 * files — so a mode the engine grows fails the typecheck here until it has a
 * name and a sentence in both languages.
 */
const MODES = ["classic", "deathmatch", "team"] as const;

/**
 * The game lengths offered, shortest first — phase 14 C2.
 *
 * A ten-card game is long, and the first real party found that out at midnight.
 * The party's own target is folded in below, so a server configured to something
 * else than one of these still shows the host what they are actually playing to
 * rather than a picker with nothing selected.
 */
const LENGTHS = [5, 8, 10, 15] as const;

/** What each length is called, by position in {@link LENGTHS}. */
const LENGTH_NAMES = ["quick", "short", "standard", "long"] as const;

/**
 * How many of every ten dealt cards may sit **outside what the host asked
 * for** — phase 19g, widened by 19e-3b and ADR 0025.
 *
 * Two kinds share the one budget: a card with no year, and — in a party that
 * narrowed its genres — a song from a friend's playlist this catalog cannot put
 * a genre to. They compete on purpose, so that narrowing a filter cannot quietly
 * multiply what ignores it.
 *
 * Three answers rather than eleven, because a host is choosing a feel and not
 * tuning a ratio. `0` turns both mechanics off and is a real answer: a party
 * drawing only from the local verified catalog never meets either anyway.
 *
 * ⚠️ **`0` is not free once a party has Spotify sources.** The deal needs
 * on-brief cards to spend between wildcards, so a narrow genre filter and a cap
 * of zero is how a deck runs dry — which `SelectionPicker` warns about and the
 * server refuses outright.
 */
const WILDCARD_CAPS = [0, 3, 7] as const;

/** The word for a cap, so the copy key stays statically checked. */
function wildcardName(perTen: number): "none" | "some" | "lots" {
  if (perTen === 0) {
    return "none";
  }
  return perTen <= 4 ? "some" : "lots";
}

/**
 * How the party is arranged: the game, the room, and the link to hand out.
 *
 * Host only — every control here is refused server-side for anybody else, so
 * rendering them for a guest would be offering a button that always fails.
 *
 * # The mode was as unreachable as the layout was
 *
 * `ConfigureRequest.mode` has existed since phase 3 and no screen sent it, which
 * is the third time this project has shipped a setting nothing could select
 * (phase 7's device layout, phase 8's… ). Team mode was the expensive one: the
 * engine has supported it since phase 4 and it had never been played.
 */
export function LobbySetup({
  lobby,
  code,
  onMode,
  onLayout,
  onLength,
  onWildcardCap,
  onApproximateYears,
  onPacing,
  busy = false,
}: {
  lobby: Lobby;
  code: string;
  onMode: (mode: string) => void;
  onLayout: (layout: string) => void;
  onLength: (cards: number) => void;
  onWildcardCap: (perTen: number) => void;
  onApproximateYears: (allow: boolean) => void;
  onPacing: (pacing: string) => void;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const couch = lobby.device_layout === "couch";
  const auto = lobby.pacing === "auto";
  // Whatever this party is actually playing to is always one of the choices,
  // even when it is not one of ours.
  const lengths = [...new Set([...LENGTHS, lobby.cards_to_win])].sort((a, b) => a - b);
  const wildcardChoices = [...new Set([...WILDCARD_CAPS, lobby.wildcards_per_ten])].sort(
    (a, b) => a - b,
  );

  return (
    <>
      <section aria-label={t("setup.game.label")}>
        <h2 className="mb-2 text-sm font-semibold">{t("setup.game.heading")}</h2>
        <div className="grid gap-2 sm:grid-cols-3">
          {MODES.map((mode) => (
            <Choice
              key={mode}
              chosen={lobby.mode === mode}
              title={t(`mode.${mode}.title`)}
              detail={t(`mode.${mode}.detail`)}
              disabled={busy}
              onChoose={() => {
                onMode(mode);
              }}
            />
          ))}
        </div>
      </section>

      <section aria-label={t("setup.length.label")}>
        <h2 className="mb-2 text-sm font-semibold">{t("setup.length.heading")}</h2>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {lengths.map((cards, index) => (
            <Choice
              key={cards}
              chosen={lobby.cards_to_win === cards}
              title={t("setup.length.cards", { count: cards })}
              detail={t(`setup.length.${LENGTH_NAMES[index] ?? "standard"}`)}
              disabled={busy}
              onChoose={() => {
                onLength(cards);
              }}
            />
          ))}
        </div>
        {/* The rule the first real party did not know it was playing: the free
            card counts, exactly like Hitster. Said here rather than discovered
            on the last round. */}
        <p className="mt-2 text-xs text-white/50">
          {t("setup.length.note", {
            count: lobby.cards_to_win,
            won: lobby.cards_to_win - 1,
          })}
        </p>
      </section>

      {/* Phase 19g. Shown as three named answers rather than a 0–10 slider,
          because a host is choosing a *feel* — "none", "some", "lots" — not
          tuning a ratio. Whatever this party is actually set to is always one
          of them, even when it is not one of ours. */}
      <section aria-label={t("setup.wildcards.label")}>
        <h2 className="mb-2 text-sm font-semibold">{t("setup.wildcards.heading")}</h2>
        <div className="grid grid-cols-3 gap-2">
          {wildcardChoices.map((perTen) => (
            <Choice
              key={perTen}
              chosen={lobby.wildcards_per_ten === perTen}
              title={t("setup.wildcards.perTen", { count: perTen })}
              detail={t(`setup.wildcards.${wildcardName(perTen)}`)}
              disabled={busy}
              onChoose={() => {
                onWildcardCap(perTen);
              }}
            />
          ))}
        </div>
        <p className="mt-2 text-xs text-white/50">{t("setup.wildcards.note")}</p>
      </section>

      {/* Phase 19e-2a, ADR 0024. Sits directly under the undated cap because
          the two are one conversation: this switch decides how many cards have
          no year, and that one decides what happens to them. A host who reads
          them apart sets both wrong.

          Two answers rather than a confidence ladder with four rungs — the
          ladder is real, but a host is choosing whether to trust an approximate
          year, not ranking sources. */}
      <section aria-label={t("setup.years.label")}>
        <h2 className="mb-2 text-sm font-semibold">{t("setup.years.heading")}</h2>
        <div className="grid grid-cols-2 gap-2">
          <Choice
            chosen={lobby.allow_approximate_years}
            title={t("setup.years.normal")}
            detail={t("setup.years.normalDetail")}
            disabled={busy}
            onChoose={() => {
              onApproximateYears(true);
            }}
          />
          <Choice
            chosen={!lobby.allow_approximate_years}
            title={t("setup.years.strict")}
            detail={t("setup.years.strictDetail")}
            disabled={busy}
            onChoose={() => {
              onApproximateYears(false);
            }}
          />
        </div>
        <p className="mt-2 text-xs text-white/50">{t("setup.years.note")}</p>
      </section>

      <section aria-label={t("setup.pace.label")}>
        <h2 className="mb-2 text-sm font-semibold">{t("setup.pace.heading")}</h2>
        <div className="grid grid-cols-2 gap-2">
          <Choice
            chosen={!auto}
            title={t("setup.pace.host.title")}
            detail={t("setup.pace.host.detail")}
            disabled={busy}
            onChoose={() => {
              onPacing("host");
            }}
          />
          <Choice
            chosen={auto}
            title={t("setup.pace.auto.title")}
            detail={t("setup.pace.auto.detail")}
            disabled={busy}
            onChoose={() => {
              onPacing("auto");
            }}
          />
        </div>
      </section>

      <section aria-label={t("setup.where.label")}>
        <h2 className="mb-2 text-sm font-semibold">{t("setup.where.heading")}</h2>
        <div className="grid grid-cols-2 gap-2">
          <Choice
            chosen={couch}
            title={t("setup.where.couch.title")}
            detail={t("setup.where.couch.detail")}
            disabled={busy}
            onChoose={() => {
              onLayout("couch");
            }}
          />
          <Choice
            chosen={!couch}
            title={t("setup.where.remote.title")}
            detail={t("setup.where.remote.detail")}
            disabled={busy}
            onChoose={() => {
              onLayout("remote");
            }}
          />
        </div>
      </section>

      {couch ? (
        // **Couch mode means somebody opens the television.** The phones stay
        // silent so the room hears one clip rather than six; a party that never
        // opens the board gets silence, and saying so here is much better than
        // discovering it in round one.
        <>
          <Setup
            heading={t("setup.tv.heading")}
            body={t("setup.tv.body")}
            url={appUrl(`/display/${code}`)}
          />
          {/* Phase 11c. A television running the app has no URL bar to paste
              that link into, so the code travels the other way — it shows six
              characters and they are typed here. */}
          <ConnectTv busy={busy} />
        </>
      ) : (
        // Remote mode's equivalent, and the reason it is a *link* rather than a
        // QR code: there is no screen in the room for anybody to point a phone
        // at, so what gets handed out is something you can paste into a call.
        <Setup
          heading={t("setup.link.heading")}
          body={t("setup.link.body")}
          url={appUrl(`/party/${code}`)}
        />
      )}
    </>
  );
}

/** One of a small set of mutually exclusive options. */
function Choice({
  chosen,
  title,
  detail,
  disabled,
  onChoose,
}: {
  chosen: boolean;
  title: string;
  detail: string;
  disabled: boolean;
  onChoose: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={chosen}
      disabled={disabled}
      onClick={onChoose}
      className={cn(
        "flex min-h-20 flex-col gap-1 rounded-lg border px-3 py-2 text-left disabled:opacity-50",
        chosen ? "border-flash-500 bg-flash-500/15" : "border-white/15 bg-stage-900",
      )}
    >
      <span className="font-medium">{title}</span>
      <span className="text-xs text-white/55">{detail}</span>
    </button>
  );
}

/**
 * What the host has to do before starting, and the URL it needs.
 *
 * **The URL is always on screen as text**, never only behind a copy button:
 * `navigator.clipboard` is undefined outside a secure context, which is exactly
 * the case for the LAN address a party on a home network is served from. A copy
 * button that silently does nothing is worse than no copy button, so it is the
 * bonus and the text is the feature.
 */
function Setup({ heading, body, url }: { heading: string; body: string; url: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  return (
    <section className="rounded-lg border border-coin-400/40 bg-coin-400/10 p-3">
      <h2 className="text-sm font-semibold">{heading}</h2>
      <p className="mt-1 text-xs text-white/60">{body}</p>
      <p className="mt-2 break-all font-mono text-sm text-flash-400">{url}</p>
      {navigator.clipboard && (
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(url).then(
              () => {
                setCopied(true);
              },
              () => {
                // Refused, which some browsers do without a gesture they
                // recognise. The link is right there to select by hand.
              },
            );
          }}
          className="mt-2 min-h-9 rounded-lg border border-white/20 px-3 text-xs font-medium"
        >
          {copied ? t("setup.copied") : t("setup.copy")}
        </button>
      )}
    </section>
  );
}
